// ============================================
// Portfolio Page - Won domains management
// ============================================

import { useState, useEffect, useMemo } from "react";
import {
  WonDomain,
  PortfolioStats,
  DomainCategory,
  DomainStatus,
} from "../../types";
import { PortfolioService } from "../../services/portfolioService";

interface PortfolioProps {
  portfolioService: PortfolioService;
  categories: DomainCategory[];
  className?: string;
}

type SortField = "domain" | "wonDate" | "purchasePrice" | "aiValuation" | "status";
type SortDirection = "asc" | "desc";

/**
 * Portfolio Component
 *
 * Manages portfolio of won domains with:
 * - Domain listing with AI valuations
 * - ROI tracking
 * - Status management (owned, listed, sold)
 * - Batch valuation
 * - Export functionality
 */
export function Portfolio({
  portfolioService,
  categories,
  className = "",
}: PortfolioProps) {
  // State
  const [domains, setDomains] = useState<WonDomain[]>([]);
  const [stats, setStats] = useState<PortfolioStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [valuating, setValuating] = useState(false);
  const [_selectedIds, _setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>("wonDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusFilter, setStatusFilter] = useState<DomainStatus | "all">("all");
  const [_editingId, setEditingId] = useState<string | null>(null);
  const [addingNew, setAddingNew] = useState(false);
  const [sellModal, setSellModal] = useState<{ id: string; domain: string } | null>(null);

  // Load data
  useEffect(() => {
    loadData();
  }, [portfolioService]);

  const loadData = () => {
    setLoading(true);
    try {
      const domainList = portfolioService.getDomains();
      setDomains(domainList);
      setStats(portfolioService.getStats(categories));
    } catch (error) {
      console.error("Failed to load portfolio:", error);
    } finally {
      setLoading(false);
    }
  };

  // Sorted and filtered domains
  const displayedDomains = useMemo(() => {
    let filtered = domains;

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((d) => d.status === statusFilter);
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null values
      if (aVal === null) aVal = sortDirection === "asc" ? Infinity : -Infinity;
      if (bVal === null) bVal = sortDirection === "asc" ? Infinity : -Infinity;

      // Compare
      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [domains, statusFilter, sortField, sortDirection]);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Add domain
  const handleAddDomain = (data: Omit<WonDomain, "id">) => {
    portfolioService.addDomain(data);
    loadData();
    setAddingNew(false);
  };

  // Delete domain
  const handleDeleteDomain = (id: string) => {
    if (confirm("Are you sure you want to delete this domain?")) {
      portfolioService.deleteDomain(id);
      loadData();
    }
  };

  // Mark as sold
  const handleSell = (id: string, price: number) => {
    portfolioService.markAsSold(id, price);
    loadData();
    setSellModal(null);
  };

  // Valuate single domain
  const handleValuate = async (id: string) => {
    setValuating(true);
    try {
      await portfolioService.getValuation(id);
      loadData();
    } catch (error) {
      console.error("Valuation failed:", error);
    } finally {
      setValuating(false);
    }
  };

  // Batch valuate
  const handleBatchValuate = async () => {
    setValuating(true);
    try {
      await portfolioService.batchValuate(10);
      loadData();
    } catch (error) {
      console.error("Batch valuation failed:", error);
    } finally {
      setValuating(false);
    }
  };

  // Export CSV
  const handleExport = () => {
    const csv = portfolioService.exportAsCSV();
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `portfolio_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate ROI color
  const getROIColor = (purchase: number, valuation: number | null) => {
    if (!valuation) return "text-green-600";
    const roi = ((valuation - purchase) / purchase) * 100;
    if (roi >= 50) return "text-green-400";
    if (roi >= 0) return "text-yellow-400";
    return "text-red-400";
  };

  if (loading) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="text-center">
          <span className="text-green-500 animate-pulse tracking-wider">
            LOADING PORTFOLIO...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-black ${className}`}>
      {/* Header */}
      <div className="px-6 py-3 border-b-2 border-green-500 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 animate-pulse" />
            <span className="text-green-400 font-bold tracking-wider">
              DOMAIN PORTFOLIO
            </span>
          </div>
          <span className="text-xs text-green-600">
            [{domains.length} DOMAINS]
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleBatchValuate}
            disabled={valuating}
            className="px-3 py-1 border border-green-700 text-green-600 text-xs hover:border-green-500 disabled:opacity-50"
          >
            {valuating ? "[VALUATING...]" : "[BATCH VALUATE]"}
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-1 border border-green-700 text-green-600 text-xs hover:border-green-500"
          >
            [EXPORT CSV]
          </button>
          <button
            onClick={() => setAddingNew(true)}
            disabled={addingNew}
            className="px-3 py-1 bg-green-500 text-black font-bold text-xs hover:bg-green-400 disabled:opacity-50"
          >
            [+ ADD DOMAIN]
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="px-6 py-3 border-b border-green-800 grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatBox label="TOTAL INVESTED" value={`$${stats.totalInvested.toLocaleString()}`} />
          <StatBox label="CURRENT VALUE" value={`$${stats.currentValue.toLocaleString()}`} />
          <StatBox
            label="UNREALIZED GAIN"
            value={`${stats.unrealizedGain >= 0 ? "+" : ""}$${stats.unrealizedGain.toLocaleString()}`}
            color={stats.unrealizedGain >= 0 ? "green" : "red"}
          />
          <StatBox
            label="ROI"
            value={`${stats.roiPercentage >= 0 ? "+" : ""}${stats.roiPercentage.toFixed(1)}%`}
            color={stats.roiPercentage >= 0 ? "green" : "red"}
          />
          <StatBox label="TOTAL DOMAINS" value={stats.totalDomains.toString()} />
        </div>
      )}

      {/* Filters */}
      <div className="px-6 py-2 border-b border-green-900 flex items-center gap-4">
        <span className="text-xs text-green-600">STATUS:</span>
        {(["all", "pending", "owned", "listed", "sold"] as const).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-2 py-1 border text-xs ${
              statusFilter === status
                ? "border-green-500 bg-green-500 text-black"
                : "border-green-800 text-green-600 hover:border-green-600"
            }`}
          >
            {status.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Add New Form */}
      {addingNew && (
        <div className="px-6 py-4 border-b border-green-700 bg-green-900/20">
          <DomainForm
            categories={categories}
            onSave={handleAddDomain}
            onCancel={() => setAddingNew(false)}
          />
        </div>
      )}

      {/* Domain Table */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 bg-black border-b border-green-700">
            <tr>
              <SortableHeader
                label="DOMAIN"
                field="domain"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="WON DATE"
                field="wonDate"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="PURCHASE"
                field="purchasePrice"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <SortableHeader
                label="VALUATION"
                field="aiValuation"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <th className="px-4 py-2 text-left text-xs text-green-600">ROI</th>
              <SortableHeader
                label="STATUS"
                field="status"
                currentSort={sortField}
                direction={sortDirection}
                onSort={handleSort}
              />
              <th className="px-4 py-2 text-left text-xs text-green-600">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {displayedDomains.map((domain) => (
              <tr
                key={domain.id}
                className="border-b border-green-900 hover:bg-green-900/20"
              >
                <td className="px-4 py-3">
                  <span className="text-green-400 font-mono">{domain.domain}</span>
                  {domain.category && (
                    <span className="ml-2 text-xs text-green-700">[{domain.category}]</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-green-500">
                  {new Date(domain.wonDate).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-sm text-green-400">
                  ${domain.purchasePrice.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm">
                  {domain.aiValuation ? (
                    <span className={getROIColor(domain.purchasePrice, domain.aiValuation)}>
                      ${domain.aiValuation.toLocaleString()}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleValuate(domain.id)}
                      disabled={valuating}
                      className="text-xs text-green-700 hover:text-green-500"
                    >
                      [GET VALUE]
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-sm">
                  {domain.aiValuation ? (
                    <span className={getROIColor(domain.purchasePrice, domain.aiValuation)}>
                      {(((domain.aiValuation - domain.purchasePrice) / domain.purchasePrice) * 100).toFixed(0)}%
                    </span>
                  ) : (
                    <span className="text-green-800">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={domain.status} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {domain.status === "owned" && (
                      <button
                        onClick={() => setSellModal({ id: domain.id, domain: domain.domain })}
                        className="px-2 py-1 border border-green-700 text-green-600 text-xs hover:border-green-500"
                      >
                        [SELL]
                      </button>
                    )}
                    <button
                      onClick={() => setEditingId(domain.id)}
                      className="px-2 py-1 border border-green-800 text-green-700 text-xs hover:border-green-600"
                    >
                      [EDIT]
                    </button>
                    <button
                      onClick={() => handleDeleteDomain(domain.id)}
                      className="px-2 py-1 border border-red-900 text-red-700 text-xs hover:border-red-600"
                    >
                      [DEL]
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Empty State */}
        {displayedDomains.length === 0 && (
          <div className="text-center py-12 text-green-600">
            NO DOMAINS IN PORTFOLIO
            <div className="mt-2 text-xs">
              Add domains manually or import from auction history
            </div>
          </div>
        )}
      </div>

      {/* Sell Modal */}
      {sellModal && (
        <SellModal
          domain={sellModal.domain}
          onSell={(price) => handleSell(sellModal.id, price)}
          onCancel={() => setSellModal(null)}
        />
      )}
    </div>
  );
}

/**
 * Stat Box Component
 */
function StatBox({
  label,
  value,
  color = "default",
}: {
  label: string;
  value: string;
  color?: "default" | "green" | "red";
}) {
  const colorClass =
    color === "green"
      ? "text-green-400"
      : color === "red"
      ? "text-red-400"
      : "text-green-300";

  return (
    <div className="text-center">
      <div className="text-xs text-green-600 tracking-wider">{label}</div>
      <div className={`text-lg font-bold ${colorClass}`}>{value}</div>
    </div>
  );
}

/**
 * Sortable Header Component
 */
function SortableHeader({
  label,
  field,
  currentSort,
  direction,
  onSort,
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
}) {
  return (
    <th
      onClick={() => onSort(field)}
      className="px-4 py-2 text-left text-xs text-green-600 cursor-pointer hover:text-green-400"
    >
      {label}
      {currentSort === field && (
        <span className="ml-1">{direction === "asc" ? "▲" : "▼"}</span>
      )}
    </th>
  );
}

/**
 * Status Badge Component
 */
function StatusBadge({ status }: { status: DomainStatus }) {
  const colors = {
    pending: "border-yellow-700 text-yellow-500",
    owned: "border-green-700 text-green-500",
    listed: "border-blue-700 text-blue-500",
    sold: "border-green-500 bg-green-500 text-black",
  };

  return (
    <span
      className={`px-2 py-0.5 border text-xs tracking-wider ${colors[status]}`}
    >
      {status.toUpperCase()}
    </span>
  );
}

/**
 * Domain Form Component
 */
function DomainForm({
  domain,
  categories,
  onSave,
  onCancel,
}: {
  domain?: WonDomain;
  categories: DomainCategory[];
  onSave: (data: Omit<WonDomain, "id">) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    domain: domain?.domain || "",
    wonDate: domain?.wonDate || new Date().toISOString().split("T")[0],
    purchasePrice: domain?.purchasePrice || 0,
    category: domain?.category || categories[0]?.name || "",
    status: domain?.status || "owned",
    notes: domain?.notes || "",
  });

  const handleSubmit = () => {
    if (!formData.domain.trim() || formData.purchasePrice <= 0) return;

    onSave({
      domain: formData.domain.trim().toLowerCase(),
      wonDate: formData.wonDate,
      purchasePrice: formData.purchasePrice,
      aiValuation: null,
      valuationDate: null,
      category: formData.category,
      status: formData.status as DomainStatus,
      notes: formData.notes,
      soldPrice: null,
      soldDate: null,
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs text-green-600 mb-1">DOMAIN</label>
          <input
            type="text"
            value={formData.domain}
            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            placeholder="example.com"
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-green-600 mb-1">WON DATE</label>
          <input
            type="date"
            value={formData.wonDate}
            onChange={(e) => setFormData({ ...formData, wonDate: e.target.value })}
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-green-600 mb-1">PURCHASE PRICE</label>
          <input
            type="number"
            value={formData.purchasePrice}
            onChange={(e) =>
              setFormData({ ...formData, purchasePrice: Number(e.target.value) })
            }
            placeholder="0"
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-green-600 mb-1">CATEGORY</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none text-sm"
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-green-500 text-black font-bold text-sm hover:bg-green-400"
        >
          SAVE DOMAIN
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 border border-green-700 text-green-600 text-sm hover:border-green-500"
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

/**
 * Sell Modal Component
 */
function SellModal({
  domain,
  onSell,
  onCancel,
}: {
  domain: string;
  onSell: (price: number) => void;
  onCancel: () => void;
}) {
  const [price, setPrice] = useState(0);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="border-2 border-green-500 bg-black p-6 w-full max-w-md">
        <h3 className="text-green-400 font-bold tracking-wider mb-4">
          MARK AS SOLD
        </h3>
        <p className="text-green-500 mb-4">
          Domain: <span className="font-mono">{domain}</span>
        </p>
        <div className="mb-4">
          <label className="block text-xs text-green-600 mb-1">SALE PRICE</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            placeholder="Enter sale price..."
            className="w-full px-3 py-2 bg-black border border-green-700 text-green-400 focus:border-green-500 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => onSell(price)}
            disabled={price <= 0}
            className="px-4 py-2 bg-green-500 text-black font-bold hover:bg-green-400 disabled:opacity-50"
          >
            CONFIRM SALE
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-green-700 text-green-600 hover:border-green-500"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}

export default Portfolio;
